/** Visual tone for status in list + receipt. */
export type StatusTone =
  | "initial"
  | "progress"
  | "completed"
  | "failed"
  | "expired";

export type StatusPresentation = {
  tone: StatusTone;
  labelKey: string;
};

/** Hex colors for status tones (list text+icon, receipt text). */
export const STATUS_TONE_COLOR: Record<StatusTone, string> = {
  initial: "#F28D19",
  progress: "#252525",
  completed: "#31C859",
  failed: "#FF3B30",
  expired: "#8a8a8a",
};

/**
 * Maps a backend operation status to tone + i18n key under `receipt.*`.
 * Cancelled shares the failed (red) tone but keeps a distinct label.
 */
export const toStatusPresentation = (
  status: string | undefined
): StatusPresentation => {
  switch ((status ?? "").toLowerCase()) {
    case "created":
    case "pending":
      return { tone: "initial", labelKey: "receipt.statusPending" };
    case "in_review":
      return { tone: "initial", labelKey: "receipt.statusInReview" };
    case "completed":
    case "funded":
    case "paid":
      return { tone: "completed", labelKey: "receipt.statusFunded" };
    case "failed":
    case "rejected":
      return { tone: "failed", labelKey: "receipt.statusFailed" };
    case "cancelled":
    case "canceled":
      return { tone: "failed", labelKey: "receipt.statusCancelled" };
    case "expired":
      return { tone: "expired", labelKey: "receipt.statusExpired" };
    default:
      return { tone: "progress", labelKey: "receipt.statusProcessing" };
  }
};

/** @deprecated Prefer `toStatusPresentation(status).labelKey`. */
export const operationStatusKey = (status: string | undefined): string =>
  toStatusPresentation(status).labelKey;

export const isOperationComplete = (status: string | undefined): boolean =>
  toStatusPresentation(status).tone === "completed";

export const isOperationFailed = (status: string | undefined): boolean => {
  const s = (status ?? "").toLowerCase();
  return s === "failed" || s === "rejected";
};
